import { useCallback, useRef, useState } from "react";
import { logger } from "@/utils/logger";

interface RateLimitConfig {
  maxRequests?: number;
  timeWindow?: number;
  cooldownPeriod?: number;
}

export const useRateLimit = (config: RateLimitConfig = {}) => {
  const {
    maxRequests = 30,
    timeWindow = 60000,
    cooldownPeriod = 1000,
  } = config;

  const [isInCooldown, setIsInCooldown] = useState(false);
  const requestCount = useRef(0);
  const lastRequestTime = useRef<number>(Date.now());
  const cooldownTimeout = useRef<NodeJS.Timeout>();

  const resetRateLimit = useCallback(() => {
    requestCount.current = 0;
    lastRequestTime.current = Date.now();
    setIsInCooldown(false);
    if (cooldownTimeout.current) {
      clearTimeout(cooldownTimeout.current);
    }
  }, []);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();

    // Reset counter if time window has passed
    if (now - lastRequestTime.current > timeWindow) {
      resetRateLimit();
      return true;
    }

    // Check if we've exceeded the rate limit
    if (requestCount.current >= maxRequests) {
      logger.warn("[useRateLimit] Rate limit exceeded");
      setIsInCooldown(true);

      // Set cooldown timeout
      cooldownTimeout.current = setTimeout(() => {
        resetRateLimit();
      }, cooldownPeriod);

      return false;
    }

    return true;
  }, [maxRequests, timeWindow, cooldownPeriod, resetRateLimit]);

  const execute = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      const now = Date.now();

      // Check if we're in cooldown
      if (isInCooldown) {
        logger.warn("[useRateLimit] Rate limit cooldown period active");
        throw new Error("Rate limit cooldown period active");
      }

      // Reset counter if time window has passed
      if (now - lastRequestTime.current > timeWindow) {
        resetRateLimit();
      }

      // Check if we've exceeded the rate limit
      if (requestCount.current >= maxRequests) {
        logger.warn("[useRateLimit] Rate limit exceeded");
        setIsInCooldown(true);

        // Set cooldown timeout
        cooldownTimeout.current = setTimeout(() => {
          resetRateLimit();
        }, cooldownPeriod);

        throw new Error("Rate limit exceeded");
      }

      // Increment request count and update last request time
      requestCount.current++;
      lastRequestTime.current = now;

      try {
        return await fn();
      } catch (error) {
        // Don't count failed requests against the rate limit
        requestCount.current = Math.max(0, requestCount.current - 1);
        throw error;
      }
    },
    [isInCooldown, maxRequests, timeWindow, cooldownPeriod, resetRateLimit],
  );

  return { execute, checkRateLimit, isInCooldown };
};
